import { useState, useRef, useLayoutEffect, useEffect, ReactNode } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Activity, Plug, Zap } from "lucide-react";
import SilkCover from "../ui/SilkCover";
import SubjectiveBuilderWidget from "./SubjectiveBuilderWidget";
import BillingCodesWidget from "./BillingCodesWidget";
import JourneyInsightsWidget from "./JourneyInsightsWidget";
import InstantEmrWidget from "./InstantEmrWidget";
import ObjectiveTrackingWidget from "./ObjectiveTrackingWidget";

type WidgetType = "subjective" | "objective" | "billing" | "journey" | "emr";

const WIDGETS: Record<WidgetType, () => JSX.Element> = {
  subjective: SubjectiveBuilderWidget,
  objective: ObjectiveTrackingWidget,
  billing: BillingCodesWidget,
  journey: JourneyInsightsWidget,
  emr: InstantEmrWidget,
};

interface Step {
  id: string;
  title: ReactNode;
  description: string;
  tagline: string;
  icon: typeof Activity;
  accent: string;
  /** Static screenshot fallback; if `widget` is set it takes precedence. */
  image: string;
  /** Optional self-playing widget rendered in the right panel instead of the image. */
  widget?: WidgetType;
}

/** Notation hero silk base — used as the active pointer pill fill. */
const SILK_BASE = "#1F3D3B";

const STEPS: Step[] = [
  {
    id: "subjective",
    title: (
      <>
        Conversation Becomes{" "}
        <span className="text-[#439CCB]">Documentation</span>
      </>
    ),
    description:
      "As you guide the session, Notation listens — capturing clinical details in real time and turning them into structured, accurate notes.",
    tagline: "You stay with the patient. We handle the note.",
    icon: Activity,
    accent: "#439CCB",
    image: "/notation/transforms/subjective.png",
    widget: "subjective",
  },
  {
    id: "objective",
    title: (
      <>
        Real-Time Audio &{" "}
        <span className="text-[#5c91a4]">Visual Tracking</span>
      </>
    ),
    description:
      "Notation captures clinical audio in real time to support accurate, in-session documentation. Computer vision features for analyzing movement, posture, and gait are in development — built to enhance clinical insight without disrupting your workflow.",
    tagline: "All that's needed is a standard laptop with an accessible mic and camera.",
    icon: Activity,
    accent: "#5c91a4",
    image: "/notation/transforms/objective.png",
    widget: "objective",
  },
  {
    id: "assessment",
    title: (
      <>
        In-Session Support for{" "}
        <span className="text-[#439CCB]">Billing & Care Planning</span>
      </>
    ),
    description:
      "Smart assistance for billing, compliance, and care — with suggestions for customizable codes, goals, and exercises.",
    tagline: "No delays. No extra steps. Just documentation that works with you.",
    icon: Activity,
    accent: "#495B25",
    image: "/notation/transforms/assessment.png",
    widget: "billing",
  },
  {
    id: "plan",
    title: (
      <>
        Your Session,{" "}
        <span className="text-[#439CCB]">Captured & Completed</span>
      </>
    ),
    description:
      "From eval to discharge, Notation delivers data-backed insights and adaptive care plans — all in-session, so there's nothing left to finish later.",
    tagline: "No more post-session admin work.",
    icon: Activity,
    accent: "#439CCB",
    image: "/notation/transforms/plan.png",
    widget: "journey",
  },
  {
    id: "plugin",
    title: (
      <>
        Instant <span className="text-[#439CCB]">EMR Transfer</span> with Plugin
      </>
    ),
    description:
      "Copy your completed SOAP notes directly to any web-based EMR with our browser extension. No manual data entry, no switching between systems — just one click and your documentation is exactly where you need it.",
    tagline: "Downloadable plugin included with your Notation account.",
    icon: Plug,
    accent: "#495B25",
    image: "/notation/transforms/plugin.png",
    widget: "emr",
  },
];

export default function NotationTransformsCare() {
  const [active, setActive] = useState(0);
  const gridRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const assetRef = useRef<HTMLDivElement>(null);
  const [line, setLine] = useState<{
    x1: number;
    y1: number;
    x2: number;
    y2: number;
  } | null>(null);

  // On mobile/tablet, scroll-spy the active pointer so the silk highlight
  // follows the section in view. Picks the last card whose top has crossed
  // the reading line (standard scroll-spy), so tall widgets don't block advances.
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023px)");
    let ticking = false;
    let lockedUntil = 0;

    const pickActive = () => {
      if (!mq.matches) return;
      if (Date.now() < lockedUntil) return;

      const targetY = window.innerHeight * 0.28;
      let bestIdx = 0;

      cardRefs.current.forEach((el, idx) => {
        if (!el) return;
        // Advance once this card's top has crossed the reading line.
        if (el.getBoundingClientRect().top <= targetY) {
          bestIdx = idx;
        }
      });

      setActive((prev) => {
        if (prev === bestIdx) return prev;
        // Brief lock so expand/collapse layout shift doesn't immediately thrash.
        lockedUntil = Date.now() + 280;
        return bestIdx;
      });
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        pickActive();
        ticking = false;
      });
    };

    const onChange = () => {
      if (mq.matches) {
        lockedUntil = 0;
        pickActive();
      }
    };

    // Defer first pick until refs are attached.
    const raf = requestAnimationFrame(() => pickActive());
    window.addEventListener("scroll", onScroll, { passive: true, capture: true });
    document.addEventListener("scroll", onScroll, { passive: true, capture: true });
    window.addEventListener("resize", onScroll);
    mq.addEventListener("change", onChange);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll, true);
      document.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
      mq.removeEventListener("change", onChange);
    };
  }, []);

  useLayoutEffect(() => {
    const compute = () => {
      const grid = gridRef.current;
      const card = cardRefs.current[active];
      const asset = assetRef.current;
      if (!grid || !card || !asset || window.innerWidth < 1024) {
        setLine(null);
        return;
      }
      const g = grid.getBoundingClientRect();
      const c = card.getBoundingClientRect();
      const a = asset.getBoundingClientRect();
      setLine({
        x1: c.right - g.left,
        y1: c.top + c.height / 2 - g.top,
        x2: a.left - g.left,
        y2: a.top + a.height / 2 - g.top,
      });
    };
    compute();
    let raf = 0;
    const onScrollResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(compute);
    };
    window.addEventListener("scroll", onScrollResize, { passive: true });
    window.addEventListener("resize", onScrollResize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScrollResize);
      window.removeEventListener("resize", onScrollResize);
    };
  }, [active]);

  return (
    <section className="pt-8 pb-20 px-4 sm:px-6 lg:px-8 fownd-window-surface">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-14 lg:mb-20"
        >
          <div className="inline-flex items-center text-[#495B25] text-base mb-5 font-semibold font-productSans">
            <Zap className="w-4 h-4 mr-2" />
            How It Works
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 font-productSans">
            How{" "}
            <span
              className="text-[#439CCB]"
              style={{ fontFamily: "'Baloo Bhaina 2', sans-serif", fontSize: "1.2em" }}
            >
              notation
            </span>{" "}
            Transforms Care
          </h2>
          <p className="mt-4 text-lg sm:text-xl font-semibold text-gray-700 font-productSans">
            Documentation takes time away from what matters most
          </p>
          <p className="mt-2 text-base text-gray-500 max-w-2xl mx-auto font-productSans font-light leading-relaxed">
            Clinicians spend valuable hours on paperwork that could be dedicated to
            patient care. There's a smarter way to handle documentation.
          </p>
        </motion.div>

        {/* Interactive list + sticky asset */}
        <div ref={gridRef} className="relative grid lg:grid-cols-2 gap-8 lg:gap-16 items-start">
          {/* Connector line from the active card to the widget (desktop) */}
          {line && (
            <svg className="hidden lg:block absolute inset-0 w-full h-full pointer-events-none z-20 overflow-visible">
              <motion.path
                key={active}
                d={`M ${line.x1} ${line.y1} C ${(line.x1 + line.x2) / 2} ${line.y1}, ${(line.x1 + line.x2) / 2} ${line.y2}, ${line.x2} ${line.y2}`}
                fill="none"
                stroke="#439CCB"
                strokeWidth={2}
                strokeLinecap="round"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 1 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              />
              <circle cx={line.x1} cy={line.y1} r={3.5} fill="#439CCB" />
              <motion.circle
                cx={line.x2}
                cy={line.y2}
                r={4.5}
                fill="#439CCB"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.42, duration: 0.3 }}
              />
            </svg>
          )}
          {/* Left — pointers */}
          <div className="flex flex-col gap-2">
            {STEPS.map((step, index) => {
              const isActive = index === active;
              const Icon = step.icon;
              return (
                <button
                  key={step.id}
                  type="button"
                  data-step-index={index}
                  ref={(el) => (cardRefs.current[index] = el)}
                  onClick={() => {
                    setActive(index);
                    // Bring the tapped card into the reading zone on mobile.
                    if (window.innerWidth < 1024) {
                      cardRefs.current[index]?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                    }
                  }}
                  className={`relative w-full text-left rounded-2xl px-5 py-5 sm:px-6 sm:py-6 overflow-hidden transition-colors duration-300 group ${
                    isActive ? "" : "hover:bg-black/[0.02]"
                  }`}
                >
                  {/* Static silk fill — matches the notation hero background */}
                  {isActive && (
                    <div className="absolute inset-0 rounded-2xl overflow-hidden">
                      <SilkCover base={SILK_BASE} className="w-full h-full" />
                    </div>
                  )}

                  <div className="relative z-10">
                    <h3
                      className={`text-2xl sm:text-3xl font-bold font-productSans leading-tight transition-colors duration-300 ${
                        isActive
                          ? "text-white"
                          : "text-gray-400 group-hover:text-gray-600"
                      }`}
                    >
                      {step.title}
                    </h3>

                    {/* Description + widget — only when active (readable on silk) */}
                    <div
                      className={`overflow-hidden transition-all duration-300 ${
                        isActive
                          ? "max-h-[900px] opacity-100 mt-3"
                          : "max-h-0 opacity-0 mt-0"
                      }`}
                    >
                      <p className="text-base sm:text-lg text-white/85 leading-relaxed">
                        {step.description}
                      </p>
                      <div className="flex items-center gap-3 mt-4 text-[#7DD3FC]">
                        <Icon className="w-5 h-5 shrink-0" />
                        <span className="text-sm sm:text-base font-medium">
                          {step.tagline}
                        </span>
                      </div>

                      {/* Inline asset on mobile only */}
                      {step.widget ? (
                        <div className="lg:hidden mt-5 aspect-[4/5]">
                          {(() => {
                            const Widget = WIDGETS[step.widget];
                            return <Widget />;
                          })()}
                        </div>
                      ) : (
                        <div className="lg:hidden mt-5 rounded-2xl overflow-hidden border border-white/15 shadow-sm bg-white">
                          <img
                            src={step.image}
                            alt=""
                            className="w-full h-auto block"
                            loading="lazy"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Right — sticky asset (desktop) */}
          <div ref={assetRef} className="hidden lg:block lg:sticky lg:top-28">
            {STEPS[active].widget ? (
              <div className="relative aspect-[4/3]">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={STEPS[active].id}
                    initial={{ opacity: 0, scale: 1.02 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.99 }}
                    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute inset-0"
                  >
                    {(() => {
                      const Widget = WIDGETS[STEPS[active].widget!];
                      return <Widget />;
                    })()}
                  </motion.div>
                </AnimatePresence>
              </div>
            ) : (
              <div className="relative aspect-[4/3] rounded-3xl overflow-hidden border border-gray-200 bg-gray-50 shadow-[0_20px_60px_-24px_rgba(31,61,59,0.3)]">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={STEPS[active].id}
                    src={STEPS[active].image}
                    alt=""
                    initial={{ opacity: 0, scale: 1.02 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.99 }}
                    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute inset-0 w-full h-full object-contain p-4"
                  />
                </AnimatePresence>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
